import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'formik';
import { useIntl, FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { SearchableTaxProfileDropdown } from '~/modules/common/components/TaxProfileDropdown';
import {
  NumberTextField,
  InvoiceTemplateDropdown
} from '~/modules/common/components';
import { getTodayForUser } from '~/modules/common/dates/today';
import FormErrorAlert from '~/modules/common/components/FormErrorAlert/FormErrorAlert';
import InvoiceCurrencySelect from './InvoiceCurrencySelect';
import ClientTaxRatesListTable from './ClientTaxRatesListTable';
import { useTaxProfiles } from './hooks';

const useStyles = makeStyles(theme => ({
  taxRateDetails: {
    padding: theme.spacing(1)
  },
  types: {
    fontSize: '0.825rem'
  },
  collapse: {
    fontSize: '0.825rem',
    color: theme.palette.primary.main,
    cursor: 'pointer'
  },
  taxRatesTable: {
    overflow: 'auto',
    backgroundColor: theme.palette.grey[300],
    padding: theme.spacing(0, 1)
  },
  gridItem: {
    marginTop: theme.spacing(1.5)
  },
  net: {
    padding: theme.spacing(2, 0.5, 0, 0)
  }
}));

const useDropDownStyles = makeStyles(theme => ({
  root: {
    '& label': {
      transform: 'translate(8px, 17px) scale(0.85)'
    },
    '& label[data-shrink="true"]': {
      transform: 'translate(8px, 7px) scale(0.65)'
    }
  },
  valueContainer: {
    alignItems: 'unset',
    height: 22,
    flexWrap: 'nowrap',
    '& > p': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  },
  input: {
    height: '1.1875em',
    display: 'flex',
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingBottom: 10,
    width: '100%'
  }
}));

const paymentTermLabel = <FormattedMessage id="client.paymentTerm" />;

export const EditClientBillingDialogForm = ({
  formik: { values, setFieldValue, errors },
  featureFlags,
  currencies,
  refetchTaxRates,
  taxRateRows,
  taxRateHeaders,
  taxRatesLoading,
  me
}) => {
  const classes = useStyles();
  const {
    taxProfile,
    invoiceTemplate,
    invoiceCurrency,
    defaultInvoicePaymentTerm
  } = values;
  const dropDownClasses = useDropDownStyles();
  const { formatMessage } = useIntl();
  const [searchTerm, setSearchTerm] = useState('');
  const handleInputChange = useCallback((_, v) => setSearchTerm(v), []);

  const { loading, taxProfiles } = useTaxProfiles({ searchTerm });

  const handleTaxProfileChange = useCallback(
    (_, value) => {
      setFieldValue('taxProfile', value);
      refetchTaxRates({
        taxProfileUri: value && value.id,
        asOfDate: getTodayForUser(me)
      });
    },
    [setFieldValue, refetchTaxRates, me]
  );

  const handleInvoiceTemplateChange = useCallback(
    (_, value) => {
      setFieldValue('invoiceTemplate', value);
    },
    [setFieldValue]
  );
  const handlePaymentTermChange = useCallback(
    value => {
      setFieldValue('defaultInvoicePaymentTerm', value);
    },
    [setFieldValue]
  );

  const handleCurrencyChange = useCallback(
    event => {
      const selectedId = event.target.value;
      const selectedCurrency = currencies.find(c => c.id === selectedId);

      setFieldValue('invoiceCurrency', selectedCurrency);
    },
    [currencies, setFieldValue]
  );

  const noOfTaxTypes = useMemo(() => taxRateRows.length, [taxRateRows]);

  const [showTaxRateDetails, setShowTaxRateDetails] = useState(false);
  const [expandText, setExpandText] = useState(
    formatMessage({ id: 'client.expandDetails' })
  );

  const toggleDetails = useCallback(() => {
    setShowTaxRateDetails(!showTaxRateDetails);
    expandText === formatMessage({ id: 'client.expandDetails' })
      ? setExpandText(formatMessage({ id: 'client.collapseDetails' }))
      : setExpandText(formatMessage({ id: 'client.expandDetails' }));
  }, [showTaxRateDetails, expandText, formatMessage]);

  const paymentTermAdornment = useMemo(
    () => (
      <span className={classes.net}>
        <FormattedMessage id="billDetails.net" />
      </span>
    ),
    [classes.net]
  );

  return (
    <>
      {!isEmpty(errors) && <FormErrorAlert />}
      <Grid container>
        <Grid item xs={12}>
          <InvoiceCurrencySelect
            currencies={currencies}
            value={invoiceCurrency}
            onChange={handleCurrencyChange}
          />
        </Grid>
        <Grid
          item
          xs={12}
          className={classes.gridItem}
          data-qe-id="PaymentTermDropdown"
        >
          <NumberTextField
            id="client.paymentTerm"
            variant="filled"
            fullWidth
            label={paymentTermLabel}
            startAdornment={paymentTermAdornment}
            min={0}
            max={25000}
            step={1}
            onChange={handlePaymentTermChange}
            value={defaultInvoicePaymentTerm}
          />
        </Grid>
        <>
          <Grid
            item
            xs={12}
            className={classes.gridItem}
            data-qe-id="TaxProfileDropdown"
          >
            <SearchableTaxProfileDropdown
              classes={dropDownClasses}
              loading={loading}
              options={taxProfiles}
              onChange={handleTaxProfileChange}
              label={formatMessage({ id: 'client.taxProfile' })}
              value={taxProfile}
              onInputChange={handleInputChange}
            />
          </Grid>
          {taxProfile && taxProfile.id ? (
            <>
              <Grid item xs={12} className={classes.taxRateDetails}>
                <Typography variant="caption" className={classes.types}>
                  <FormattedMessage
                    id="client.taxTypes"
                    values={{ n: noOfTaxTypes }}
                  />
                </Typography>
                {noOfTaxTypes ? (
                  <Typography
                    variant="caption"
                    className={classes.collapse}
                    onClick={toggleDetails}
                  >
                    {' - '}
                    {expandText}
                  </Typography>
                ) : null}
              </Grid>
              {showTaxRateDetails ? (
                <Grid
                  item
                  xs={12}
                  className={classes.taxRatesTable}
                  data-qe-id="TaxRatesTable"
                >
                  <ClientTaxRatesListTable
                    taxRatesLoading={taxRatesLoading}
                    taxRateHeaders={taxRateHeaders}
                    taxRateRows={taxRateRows}
                  />
                </Grid>
              ) : null}
            </>
          ) : null}
        </>
        <Grid
          item
          xs={12}
          className={classes.gridItem}
          data-qe-id="InvoiceTemplateDropdown"
        >
          <InvoiceTemplateDropdown
            value={invoiceTemplate}
            onChange={handleInvoiceTemplateChange}
          />
        </Grid>
      </Grid>
    </>
  );
};

EditClientBillingDialogForm.propTypes = {
  formik: PropTypes.object,
  featureFlags: PropTypes.object,
  currencies: PropTypes.array,
  refetchTaxRates: PropTypes.func,
  taxRateRows: PropTypes.array,
  taxRateHeaders: PropTypes.array,
  taxRatesLoading: PropTypes.bool,
  me: PropTypes.object
};

export default connect(EditClientBillingDialogForm);
